// <s-contact-form> — progressive validation for the native Shopify contact form it wraps.
// With JS, native bubbles are replaced by inline messages: each required control carries
// data-msg-required (and data-msg-email for e-mails) and points to its error <p> through
// aria-describedby. Without JS the browser's own `required` validation still applies.

class SContactForm extends HTMLElement {
  #ac = null;

  connectedCallback() {
    const form = this.querySelector('form');
    if (!form) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    form.noValidate = true;
    form.addEventListener('submit', (event) => this.#onSubmit(event, form), { signal });
    form.addEventListener('input', (event) => this.#clear(event.target), { signal });
    form.addEventListener('change', (event) => this.#clear(event.target), { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #fields(form) {
    return [...form.querySelectorAll('[data-msg-required]')];
  }

  #errorNode(field) {
    const id = field.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  }

  #message(field) {
    if (field.type === 'checkbox') return field.checked ? '' : field.dataset.msgRequired;
    const value = field.value.trim();
    if (!value) return field.dataset.msgRequired;
    if (field.type === 'email' && !field.checkValidity()) return field.dataset.msgEmail || field.dataset.msgRequired;
    return '';
  }

  #show(field, message) {
    const node = this.#errorNode(field);
    if (node) node.textContent = message;
    if (message) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
  }

  #clear(field) {
    if (!(field instanceof HTMLElement) || !field.hasAttribute('data-msg-required')) return;
    if (field.getAttribute('aria-invalid') !== 'true') return;
    if (!this.#message(field)) this.#show(field, '');
  }

  #onSubmit(event, form) {
    let firstInvalid = null;
    for (const field of this.#fields(form)) {
      const message = this.#message(field);
      this.#show(field, message);
      if (message && !firstInvalid) firstInvalid = field;
    }
    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
    }
  }
}

if (!customElements.get('s-contact-form')) customElements.define('s-contact-form', SContactForm);
